import {
  OptimizelyDecision,
  ReactSDKClient,
  enums,
} from '@optimizely/react-sdk';

import { tracker } from '@tracking/tracker';
import {
  optimizelyDecisionCalledEvent,
  optimizelyDecisionCalledMetadataEntity,
  optimizelyEventTrackedEvent,
  optimizelyEventTrackedMetadataEntity,
} from '@tracking/trackingEvents';

const minDuration = 1000 * 60 * 5;

interface Timestamps {
  [key: string]: number;
}

interface DecisionEvent {
  type: string;
  userId: string;
  decisionInfo: OptimizelyDecision;
}

interface TrackEvent {
  eventKey: string;
  eventTags: {
    revenue: number;
    value: number;
  };
  userId: string;
}

const onDecision = (() => {
  const timestamps: Timestamps = {};
  const genKey = (flagKey: string, variationKey: string | null) =>
    `${flagKey}+${variationKey}`;

  return ({ type, userId, decisionInfo }: DecisionEvent) => {
    const analyticsClient = tracker;
    if (type === 'flag') {
      const { flagKey, enabled, variationKey } = decisionInfo;
      const decisionKey = genKey(flagKey, variationKey);

      const ts = Date.now();
      if (ts - timestamps[decisionKey] < minDuration) return;

      analyticsClient.track(optimizelyDecisionCalledEvent(), {
        contexts: [
          optimizelyDecisionCalledMetadataEntity({
            experiment_key: flagKey,
            enabled,
            variation_key: variationKey as string,
            user_id: userId,
          }),
        ],
      });

      timestamps[decisionKey] = Date.now();
    }
  };
})();

const onTrack = (() => {
  const timestamps: Timestamps = {};

  return (event: TrackEvent) => {
    const analyticsClient = tracker;
    const { eventKey, eventTags, userId } = event;
    const { revenue, value } = eventTags;

    const ts = Date.now();
    if (ts - timestamps[eventKey] < minDuration) return;

    analyticsClient.track(optimizelyEventTrackedEvent(), {
      contexts: [
        optimizelyEventTrackedMetadataEntity({
          event_key: eventKey,
          user_id: userId,
          value,
          revenue,
        }),
      ],
    });

    timestamps[eventKey] = Date.now();
  };
})();

const initNotificationListeners = (optimizelyClient: ReactSDKClient) => {
  optimizelyClient.notificationCenter.addNotificationListener(
    enums.NOTIFICATION_TYPES.DECISION,
    onDecision
  );

  optimizelyClient.notificationCenter.addNotificationListener(
    enums.NOTIFICATION_TYPES.TRACK,
    onTrack
  );
};

export { initNotificationListeners };
